import * as React from 'react';
import { ModalProps } from '../../modal';
import { ScrollableOverlay } from '../../scrollable-overlay/scrollable-overlay';
import overlayStyles, { ScrollableOverlayBodyStyle } from '../../../styles/scrollable-overlay';
import { classes, style } from 'typestyle';
import { px } from 'csx';
import { ComponentProps, deps, inject, observer } from '../../../lib/component';
import { rebrand } from '../../../theme/color';
import { Link } from '../atoms/Link';
import { GoogleButton } from '../../user/google-button';
import { spacing } from '../../../theme/spacing';
import { LOG_IN_ROUTE, SIGN_UP_ROUTE } from '../../../constants';
import { flexRoot, vertical } from 'csstips';

const styles = {
  wrapper: style({
    display: 'block',
    maxWidth: px(440),
  }),
  heading: style({
    fontSize: px(32),
  }),
  body: style(ScrollableOverlayBodyStyle, {
    padding: '1.5rem 0 0',
    borderTop: `1px solid ${rebrand.light1.toString()}`,
    textAlign: 'center',
    $nest: {
      '& > p': {
        margin: 0,
      },
    },
  }),
  largeText: style({
    fontSize: px(20),
  }),
  actions: style(flexRoot, vertical, {
    marginTop: spacing.LARGE,
    gap: spacing.XSMALL,
    $nest: {
      '& p': {
        margin: 0,
      },
    },
  }),
  button: style({
    height: px(48),
  }),
  logInPrompt: style({
    color: rebrand.contrast[40].toString(),
    marginBottom: 0,
    fontSize: px(12),
    $nest: {
      '& a': {
        textDecoration: 'underline',
        color: rebrand.contrast[50].toString(),
      },
    },
  }),
};

export type CreateAccountPromptModalProps = ModalProps & { className?: string };

export const CreateAccountPromptModal: React.FC<CreateAccountPromptModalProps & ComponentProps> = inject(deps)(
  observer(({ show, onRequestClose, model: { router }, controller: { analytics } }) => {
    const onSignupClick = () => {
      analytics.sendMixpanel('User clicks signup button (create account prompt modal)');
      router.push(SIGN_UP_ROUTE);
      onRequestClose();
    };

    const onLoginClick = () => {
      analytics.sendMixpanel('User clicks login link (create account prompt modal)');
      onRequestClose();
    };

    return (
      <ScrollableOverlay
        className={styles.wrapper}
        show={show}
        onRequestClose={onRequestClose}
        data-test-create-account-prompt-modal
      >
        <div className={overlayStyles.header}>
          <h3 className={classes(overlayStyles.title, styles.heading)}>Create a Lickd account</h3>
        </div>

        <div className={styles.body}>
          <p className={styles.largeText}>
            With an account, you can license music on Lickd and receive notifications when new tracks and artists you
            like are available
          </p>

          <div className={styles.actions}>
            <p>
              <strong>Sign up with a YouTube channel</strong>
            </p>

            <GoogleButton className={styles.button} onClick={onSignupClick} data-test-signup-button-google />

            <p className={styles.logInPrompt}>
              Or{' '}
              <Link href={LOG_IN_ROUTE} onClick={onLoginClick}>
                log in
              </Link>{' '}
            </p>
          </div>
        </div>
      </ScrollableOverlay>
    );
  })
);
